import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, Trash2, Star, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { ClientLocation, GeocodedAddress, LOCATION_LABELS } from "@/lib/location/types";
import { LocationMiniMap } from "./LocationMiniMap";

interface ClientLocationsEditorProps {
  locations: ClientLocation[];
  onAdd: (location: Partial<ClientLocation>) => Promise<ClientLocation | null>;
  onUpdate: (id: string, updates: Partial<ClientLocation>) => Promise<ClientLocation>;
  onDelete: (id: string) => Promise<void>;
  onSetPrimary: (id: string) => Promise<void>;
  compact?: boolean;
}

interface NewLocationForm {
  label: string;
  street_address: string;
  street_address_2: string;
  city: string;
  state: string;
  zip_code: string;
  latitude?: number;
  longitude?: number;
}

const EMPTY_FORM: NewLocationForm = {
  label: "Home",
  street_address: "",
  street_address_2: "",
  city: "",
  state: "",
  zip_code: "",
};

export function ClientLocationsEditor({
  locations,
  onAdd,
  onUpdate,
  onDelete,
  onSetPrimary,
  compact = false,
}: ClientLocationsEditorProps) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewLocationForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const handleAutocomplete = (address: GeocodedAddress) => {
    setForm({
      ...form,
      street_address: address.street_address,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      latitude: address.latitude,
      longitude: address.longitude,
    });
  };

  const handleSave = async () => {
    if (!form.street_address) return;
    setSaving(true);
    try {
      await onAdd({
        label: form.label,
        street_address: form.street_address,
        street_address_2: form.street_address_2 || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
        geocode_status: form.latitude ? "success" : "pending",
        geocode_source: form.latitude ? "mapbox" : null,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
    } catch (error) {
      console.error("Error adding location:", error);
    } finally {
      setSaving(false);
    }
  };

  const geocodeIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-3 w-3 text-green-500" />;
      case "failed":
        return <AlertCircle className="h-3 w-3 text-destructive" />;
      case "pending":
        return <Loader2 className="h-3 w-3 text-muted-foreground animate-spin" />;
      default:
        return <MapPin className="h-3 w-3 text-muted-foreground" />;
    }
  };

  return (
    <div className="space-y-3">
      {locations.map((loc) => (
        <Card key={loc.id} className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {geocodeIcon(loc.geocode_status)}
                <span className="text-sm font-medium">{loc.label}</span>
                {loc.is_primary && (
                  <Badge variant="secondary" className="text-xs">Primary</Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {[loc.street_address, loc.city, loc.state, loc.zip_code]
                  .filter(Boolean)
                  .join(", ")}
              </p>
              {!compact && loc.latitude && loc.longitude && (
                <div className="mt-2">
                  <LocationMiniMap lat={loc.latitude} lng={loc.longitude} height={120} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {!loc.is_primary && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onSetPrimary(loc.id)}
                  title="Set as primary"
                >
                  <Star className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                onClick={() => onDelete(loc.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </Card>
      ))}

      {showForm ? (
        <Card className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <AddressAutocomplete
                label="Address"
                onSelect={handleAutocomplete}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Street Address</Label>
              <Input
                value={form.street_address}
                onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                placeholder="123 Main St"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Apt/Suite</Label>
              <Input
                value={form.street_address_2}
                onChange={(e) => setForm({ ...form, street_address_2: e.target.value })}
                placeholder="Apt 4B"
              />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-xs">State</Label>
                <Input
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  maxLength={2}
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">ZIP</Label>
                <Input
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Label</Label>
              <Select value={form.label} onValueChange={(v) => setForm({ ...form, label: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_LABELS.map((l) => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!form.street_address || saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Add Location
            </Button>
          </div>
        </Card>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setShowForm(true)}
        >
          <Plus className="h-4 w-4 mr-1" /> Add Location
        </Button>
      )}
    </div>
  );
}
